import { LinkField } from '@sitecore-jss/sitecore-jss-nextjs';
import { LinkDataLayerObject, CarouselClickDataLayerObject } from './_data-layer-types';
import { pageNavigationDataLayerPush } from './_helpers';

/**
 * Properties passed to Link Data Layer override callback
 */
export interface OnLinkDataLayerPushProps {
  /**
   * The mouse event that triggerred the callback.
   */
  event: React.MouseEvent<HTMLElement>;
  /**
   * Data that will be pushed to the data layer when the callback completes.
   */
  dataLayerInfo: LinkDataLayerObject | null;
  /**
   * Optional @see LinkField describing the source link
   */
  linkField?: LinkField;
  /**
   * When true, data layer push processing will not occur.
   */
  readonly defaultPrevented: boolean;
  /**
   * Called to set @see defaultPrevented to true.
   */
  preventDefault: () => void;
}

/**
 * Simple implementation of @see OnLinkDataLayerPushProps that supports event suppression.
 */
export class OnLinkDataLayerPushPropsImpl implements OnLinkDataLayerPushProps {
  event: React.MouseEvent<HTMLElement>;
  dataLayerInfo: LinkDataLayerObject | null;
  linkField?: LinkField;
  defaultPrevented: boolean;

  constructor(
    e: React.MouseEvent<HTMLElement>,
    d: LinkDataLayerObject | null,
    l?: LinkField | undefined
  ) {
    this.event = e;
    this.dataLayerInfo = d;
    this.linkField = l;
    this.defaultPrevented = false;
    this.preventDefault = this.preventDefault.bind(this);
  }

  preventDefault(): void {
    this.defaultPrevented = true;
  }
}

/**
 * Used to override default LinkA11yWrapper data layer push behavior.
 */
export type OnLinkDataLayerPush = (props: OnLinkDataLayerPushProps) => void;

/**
 * Standard OnLinkDataLayerPush callback used to suppress LinkA11yWrapper data layer interactions.  Always returns false.
 * @param props Used to suppress default event handling.
 */
export const alwaysSuppressLinkDataLayerPush = ({ preventDefault }: OnLinkDataLayerPushProps) =>
  preventDefault();

/**
 *  Helper method for processing clicks on CTAs located on a Carousel
 */
export const onCarouselCtaDataLayerPush = ({
  pushEvent,
  index,
  carouselType,
  items,
  carouselText,
}: {
  pushEvent: OnLinkDataLayerPushProps;
  index: number;
  carouselType: string;
  carouselText?: string;
  items?: { name: string }[];
}) => {
  const carouselCtaDataLayer = pushEvent.dataLayerInfo as CarouselClickDataLayerObject;
  carouselCtaDataLayer.event = 'e_carouselClick';
  carouselCtaDataLayer.clickAction = 'carousel - CTA click';
  carouselCtaDataLayer.contentPosition = index + 1;
  carouselCtaDataLayer.carouselType = carouselType;
  if (items) {
    carouselCtaDataLayer.contentName = items[index].name;
  } else {
    carouselCtaDataLayer.contentName = carouselText ?? 'N/A';
  }
};

/**
 * Clean menu locations used for page navigation datalayer push
 */
export const menuLocation: string[] = [
  'top header',
  'header',
  'footer',
  'sidebar',
  'anchor',
  'search',
  'breadcrumbs',
  'quick links list',
];

/**
 * pushProps.dataLayerInfo.componentType data cleaner function
 * @param componentType
 * @returns
 */
export const cleanMenuLocation = (componentType?: string) => {
  const componentSegments = componentType?.split('/');
  let matchedMenuLocation;
  componentSegments?.some((segment) => {
    const menuIndex = menuLocation.findIndex((menu) => {
      return segment === menu;
    });
    if (segment === menuLocation[menuIndex]) {
      matchedMenuLocation = menuLocation[menuIndex];
    }
    return segment === menuLocation[menuIndex];
  });
  return matchedMenuLocation;
};

/**
 * Datalayer helper handler for page navigation clicks
 * @param param0
 */
export const pageNavigationDataLayerHandler = (
  pushProps: OnLinkDataLayerPushProps,
  componentTypeOverride?: string
) => {
  const navigationDataLayer = pushProps.dataLayerInfo;
  const itemName = navigationDataLayer ? navigationDataLayer.clickText : 'undefined';
  const componentType = navigationDataLayer ? navigationDataLayer.componentType : 'undefined';
  const cleanComponentType = cleanMenuLocation(componentType);
  let menuType = componentType;
  if (componentTypeOverride) {
    menuType = componentTypeOverride;
  } else if (cleanComponentType) {
    menuType = cleanComponentType;
  }

  if (pageNavigationDataLayerPush(itemName, menuType)) {
    pushProps.preventDefault();
  }
};

export const logoNavigationGtmPush = (logoLocation: string, logo = 'logo') => {
  pageNavigationDataLayerPush(logo, logoLocation);
};
